<template>
  <div class="c-saved-canvas-management w-full h-full">
    <CBulkToolbar
      v-if="tableRef && tableRef?.selectedRowsCount"
      class="min-h[68px] px-5"
      :count="tableRef.pageRowsCount"
      :disabled="isBulkToolbarDisabled"
      :moreActions="{
        addTags: true,
        removeTags: true,
        manageAttributes: true,
        delete: true,
        manageDistribution: false,
      }"
      :selectedCount="tableRef.selectedRowsCount"
      showDelete
      showMore
      @addTags="bulkAddTags"
      @delete="bulkDelete"
      @manageAttributes="bulkUpdateMetadata"
      @manageDistribution="onBulkDistribution"
      @removeTags="bulkRemoveTags"
      @toggleSelect="tableRef.selectAll"
    />
    <CSavedCanvasesManagementToolbar
      v-show="!tableRef?.selectedRowsCount"
      ref="toolbarWrapper"
      :custom-filter-components="dataFilterComponents"
      :hide-create="isCanvasCreationDisabled"
      :hide-view="!launchDarkly.allow_canvases_tables_columns_settings"
      :is-loading-items="isLoading"
      :metadata-filter-values="filters"
      :search="search"
      :tableColumnsSettings="tableColumnsSettings"
      @configure-smart-folders="smartFoldersRef?.showConfigurationModal?.()"
      @create:canvas="emit('create')"
      @move="onColumnsMove"
      @restoreColumns="restoreColumns"
      @search="onTableChange('searchChanged', $event)"
      @select="onColumnsSelect"
      @update:filter-values="onTableChange('filterValuesChanged', $event)"
    />

    <SmartFolders
      v-if="view === 'smartFolders'"
      ref="smartFoldersRef"
      :is-any-type-of-admin="isAnyTypeOfAdmin"
      :toolbar-height="canvasManagementToolbarHeight"
      @open="(e, canvas) => emit('open', canvas, isModifierClick(e))"
    />
    <CTable
      v-if="view === 'normal'"
      ref="tableRef"
      v-model:selected-rows="selectedRows"
      class="mt-3"
      :columns="launchDarkly.allow_canvases_tables_columns_settings ? columnsFromSettings : columns"
      :data="filteredData"
      :initialSorting="[{ id: 'modified_at', desc: true }]"
      :loading="isLoadingTable"
      :page-index="fetchingParams.page - 1"
      :page-size="fetchingParams.page_size"
      :page-sizes="DEFAULT_PAGE_SIZE_OPTIONS"
      :scroller-class="noTabs ? 'w-full overflow-auto max-h-[calc(100vh-314px)]' : undefined"
      :scroller-style="{
        height: `calc(100vh - 320px - ${canvasManagementToolbarHeight || 0}px + ${noTabs ? 36 : 0}px)`,
      }"
      :selectable="launchDarkly.allow_bulk_actions_canvases_files"
      server-side
      :total-count="totalCount"
      @page-changed="onTableChange('pageChanged', $event)"
      @page-size-changed="onTableChange('pageSizeChanged', $event)"
      @sort-changed="onTableChange('sortChanged', $event)"
    />
    <CCanvasAccountModal
      v-if="isAccountModalVisible"
      :accounts="accounts"
      :accountsLoading="accountsLoading"
      @close="resolveAccountSelection(null)"
      @search="(value) => emit('account-search', value)"
      @select="(account) => resolveAccountSelection(account)"
    />
    <slot />
  </div>
</template>

<script setup lang="tsx">
import { computed, onBeforeUnmount, onMounted, ref, inject, watch, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { type ColumnDef, createColumnHelper, type PaginationState, type SortingState } from '@tanstack/table-core'
import { isEqual, merge, omit } from 'lodash-es'
import { useElementSize, useLocalStorage, StorageSerializers } from '@vueuse/core'

import { DEFAULT_PAGE_SIZE_OPTIONS } from '@lib/constants/tables.const'
import CTable from '@lib/components/CTable/CTable.vue'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import CTableTag from '@lib/components/CTableTag/CTableTag.vue'
import CTableMore from '@lib/components/CTableMore/CTableMore.vue'
import CSavedCanvasesManagementToolbar from '@lib/components/savedCanvases/CSavedCanvasesManagementToolbar.vue'
import CSavedCanvasesManagementActionsCell from '@lib/components/savedCanvases/CSavedCanvasesManagementActionsCell.vue'
import CSavedCanvasesManagementUsedTemplateCell from '@lib/components/savedCanvases/CSavedCanvasesManagementUsedTemplateCell.vue'
import CSavedCanvasesManagementOwnerCell, {
  type Owner,
} from '@lib/components/savedCanvases/CSavedCanvasesManagementOwnerCell.vue'
import CSavedCanvasesManagementIndicatorsCell from '@lib/components/savedCanvases/CSavedCanvasesManagementIndicatorsCell.vue'
import CCanvasDeleteDialogContent from '@lib/components/canvases/CCanvasDeleteDialogContent.vue'
import CBulkToolbar from '@lib/components/CBulkToolbar/CBulkToolbar.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useConfirmation from '@lib/composables/useConfirmation'
import useCrmShape from '@canvas-builder/composables/useCrmShape'
import useThemeVars from '@lib/composables/useThemeVars'
import type { PaginatedData } from '@lib/types/paginatedData'
import type { GetCanvasesParams } from '@lib/types/canvases'
import { isPitcherWkWebView } from '@lib/util/userAgents'
import { isModifierClick } from '@lib/util/navigation.util'
import { getVisibleColumns } from '@lib/util/canvas-manager.util'
import { formatDateTime } from '@lib/util/date'
import useCanvasesManagementBulk from '@lib/components/canvases/CanvasesManagementBulk.use.tsx'
import useShareCanvas, {
  type SharingHandler,
} from '@lib/components/savedCanvases/CSavedCanvasesManagementShareCanvas.use'
import type { GetInstanceMetadataTemplatesPayload } from '@sdk/payload.types'
import {
  type CanvasRetrieve,
  type CanvasCreateRequest,
  type PaginatedMetadataTemplateList,
  type PatchedCanvasUpdateRequest,
  type User,
} from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import useToast from '@lib/composables/useToast'
import { normalizeFilterParams } from '@lib/components/filters/filter.util'
import { useTableColumnsSettings } from '@lib/composables/tableColumnsSettings.use'
import usePagination from '@lib/composables/useCanvasTablesPagination'
import useSuggestedTags from '@lib/composables/suggestedTags.use'
import SmartFolders from '@lib/components/savedCanvases/smartFolders/SmartFolders.vue'
import useSmartFolders from '@lib/components/savedCanvases/smartFolders/SmartFolders.use'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import CSwitch from '@lib/components/CSwitch/CSwitch.vue'
import CThumbnail from '@lib/components/CThumbnail/CThumbnail.vue'
import CCanvasAccountModal from './CCanvasAccountModal.vue'
import useCanvasLocks from '@lib/composables/useCanvasLocks'
import { useCanvasStandardFilters } from '@lib/composables/useCanvasStandardFilters'

interface Account {
  id?: string
  value?: string
  name?: string
  label?: string
}

const props = defineProps<{
  updater: (
    params: PatchedCanvasUpdateRequest & { id: CanvasRetrieve['id']; fields?: string },
  ) => Promise<CanvasRetrieve>
  creator: (params: Omit<CanvasCreateRequest, 'instance_id'>) => Promise<CanvasRetrieve>
  fetcher: (
    params: GetCanvasesParams & { filters?: Record<string, any>; fields?: string },
  ) => Promise<PaginatedData<CanvasRetrieve>>
  deleter: (id: CanvasRetrieve['id']) => Promise<CanvasRetrieve>
  byIdFetcher: (params: { id: CanvasRetrieve['id']; fields?: string }) => Promise<CanvasRetrieve>
  sharer: SharingHandler
  isCRM: boolean
  isCanvasCreationDisabled?: boolean
  isSharedLinkCreationDisabled?: boolean
  hideEdit?: boolean
  isAnyTypeOfAdmin?: boolean
  downloadCanvas: (canvas: CanvasRetrieve, fileFormat?: 'pdf' | 'pptx') => Promise<void>
  myUser?: User | null
  noTabs?: boolean
  storageKey?: string
  accounts: { label: string; value: string }[]
  accountsLoading: boolean
  track?: (eventName: string, payload: any) => void
  checkTemplatesAccess?: (params: { templateIds: string[] }) => Promise<{ accessibleTemplateIds: string[] }>
}>()
const emit = defineEmits<{
  open: [canvas: CanvasRetrieve, newTab?: boolean]
  openTemplate: [canvasId: CanvasRetrieve['id'], templateId: CanvasRetrieve['id']]
  createFromTemplate: [templateId: CanvasRetrieve['id']]
  edit: [canvas: CanvasRetrieve]
  share: [canvas: CanvasRetrieve]
  create: []
  'account-search': [value: string]
}>()
const tableRef = ref<InstanceType<typeof CTable> | null>(null)
const smartFoldersRef = ref<InstanceType<typeof SmartFolders> | null>(null)
const toolbarWrapper = ref<HTMLElement | null>(null)
const selectedRows = ref<Record<string, boolean>>({})
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const { isSmartFoldersView, handleSearch } = useSmartFolders()
const view = computed(() =>
  launchDarkly.value.enable_smart_folders && isSmartFoldersView.value ? 'smartFolders' : 'normal',
)
const metadataFetcher = inject<
  (params?: GetInstanceMetadataTemplatesPayload) => Promise<PaginatedMetadataTemplateList>
>('instanceMetadataTemplatesFetcher')

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const { t } = useI18n()
const { getDefaultCrmShape } = useCrmShape()
const themeVars = useThemeVars()
const { toast } = useToast()
const confirmation = useConfirmation()
const { fetchSuggestedInstanceTags } = useSuggestedTags()
const {
  bulkDeleteDialog,
  bulkAddTagsDialog,
  bulkRemoveTagsDialog,
  bulkUpdateMetadataDialog,
  bulkUpdateDistributionDialog,
} = useCanvasesManagementBulk()
const { copySharingUrlToClipboard } = useShareCanvas(props.sharer, {
  useShortUrl: !!launchDarkly.value.use_short_url_for_shared_link,
  contextUpdater: async (id: CanvasRetrieve['id']) => {
    const canvas = await props.byIdFetcher({ id, fields: 'id,context,account' })
    if (!canvas) return
    const currentContext = canvas.context

    props.updater({
      id,
      fields: 'id,context',
      context: merge(
        {},
        currentContext ?? {},
        getDefaultCrmShape({ accountName: canvas.account?.name, myUser: props.myUser ?? null }),
      ),
    })
  },
})
const DEFAULT_ORDERING = '-modified_at'
const initialFilters: Record<string, any> = {}

const {
  search,
  page,
  pageSize,
  selectedFilters: filters,
  ordering,
} = usePagination({
  storageKey: props.storageKey,
  initialFilters,
  initialOrdering: DEFAULT_ORDERING,
})
const {
  isLockedBySomeoneElse,
  lockCanvas,
  unlockCanvas,
  isCanvasLocked,
  getCanvasLockTooltip,
  getCanvasLock,
  isOrgAdmin,
} = useCanvasLocks()
const sharingInProgress = ref<Set<CanvasRetrieve['id']>>(new Set())
const copyingInProgress = ref<Set<CanvasRetrieve['id']>>(new Set())
const deletingInProgress = ref<Set<CanvasRetrieve['id']>>(new Set())
const downloadingInProgress = ref<Set<CanvasRetrieve['id']>>(new Set())
const asyncDownloadStarted = ref<Set<CanvasRetrieve['id']>>(new Set())

const pollingCanvases = ref(new Set<string>())
const pollingInterval = ref<NodeJS.Timeout | null>(null)
const POLLING_INTERVAL_MS = 30000

const canvasOwnerIdColumnStatus = ref<Record<CanvasRetrieve['id'], 'editing' | 'updating'>>({})
const editingColumn = ref<{ id: CanvasRetrieve['id']; column: string } | null>(null)
const fetchedData = ref<CanvasRetrieve[]>([])
const isLoading = ref(false)
const accessibleTemplateIds = ref<Set<string>>(new Set())
const availableTemplates = ref<CanvasRetrieve[]>([])
const isCheckingTemplateAccess = ref(false)
const isBulkDeleting = ref(false)
const isBulkUpdating = ref(false)
const totalCount = ref(0)
const localCall = useLocalStorage<any | null>('call', null, {
  deep: true,
  listenToStorageChanges: true,
  serializer: StorageSerializers.object,
})
const accountFilterId = computed<string | undefined>(() => {
  const enabled = !!pitcherSettings.value?.canvas?.enable_canvases_account_filtering
  const id = localCall.value?.selectedAccount?.Id
  return enabled && id ? id : undefined
})

async function startPollingCanvas(canvasId: string) {
  await refreshCanvasMetadata(canvasId)

  const canvas = fetchedData.value.find((c) => c.id === canvasId)

  if (!canvas) return

  const metadata = (canvas.metadata || {}) as Record<string, any>
  const hasUrls = metadata?.pdf_url && metadata?.pptx_url
  const hasJob = metadata?.job_id

  if (hasJob && hasUrls) {
    stopPollingCanvas(canvas.id)
  }

  if (hasJob && !hasUrls) {
    pollingCanvases.value.add(canvasId)

    if (!pollingInterval.value && pollingCanvases.value.size > 0) {
      pollingInterval.value = setInterval(pollAsyncDownloads, POLLING_INTERVAL_MS)
    }
  }
}

function stopPollingCanvas(canvasId: string) {
  pollingCanvases.value.delete(canvasId)
  asyncDownloadStarted.value.delete(canvasId)

  if (pollingCanvases.value.size === 0 && pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
}

async function checkAndRestartPolling(canvas: CanvasRetrieve) {
  if (!launchDarkly.value.enable_scale_content) return

  await startPollingCanvas(canvas.id)
}

async function pollAsyncDownloads() {
  if (pollingCanvases.value.size === 0) return

  try {
    const canvasIds = Array.from(pollingCanvases.value)

    const promises = canvasIds.map(async (canvasId) => {
      try {
        await refreshCanvasMetadata(canvasId)

        const canvas = fetchedData.value.find((c) => c.id === canvasId)

        if (!canvas) {
          stopPollingCanvas(canvasId)
          return
        }

        const metadata = (canvas.metadata || {}) as Record<string, any>
        const hasUrls = !!metadata.pdf_url && !!metadata.pptx_url

        if (hasUrls) stopPollingCanvas(canvasId)
      } catch (error) {
        console.error(`Failed to poll canvas ${canvasId}:`, error)
        stopPollingCanvas(canvasId)
      }
    })

    await Promise.all(promises)
  } catch (error) {
    console.error('Error during async download polling:', error)
  }
}

function hasSystemTag(tags?: string[]): boolean {
  const systemTags = ['home', 'share', 'sharebox']
  return (
    tags?.some((tag: string) => {
      const lowerTag = tag.toLowerCase()
      return systemTags.includes(lowerTag)
    }) ?? false
  )
}

const templateOptions = computed(() =>
  availableTemplates.value.map((t) => ({ label: t.name, value: t.id })).sort((a, b) => a.label.localeCompare(b.label)),
)

async function fetchTemplates() {
  try {
    const { results } = await props.fetcher({
      is_template: true,
      is_section: false,
      page: 1,
      page_size: 1000,
      fields: 'id,name',
    })
    availableTemplates.value = results
  } catch (e) {
    console.error('Failed to fetch templates for filter:', e)
  }
}

const { filterComponents: dataFilterComponents } = useCanvasStandardFilters({
  includeTemplateFilter: true,
  includeOwnerFilter: props.isAnyTypeOfAdmin ?? false,
  templateOptions,
})

const { height: canvasManagementToolbarHeight } = useElementSize(toolbarWrapper)

// Function to check template access for all templates in the current data
const checkTemplateAccessForCurrentData = async () => {
  // Only check template access if distributed canvas feature is enabled
  if (!launchDarkly.value.enable_canvas_core_distributions) {
    // If feature is disabled, assume all templates are accessible
    accessibleTemplateIds.value = new Set()
    return
  }

  if (!fetchedData.value.length || isCheckingTemplateAccess.value || !props.checkTemplatesAccess) return

  try {
    isCheckingTemplateAccess.value = true
    const templateIds = [...new Set(fetchedData.value.map((canvas) => canvas.template?.id).filter(Boolean) as string[])]

    if (templateIds.length === 0) {
      accessibleTemplateIds.value = new Set()
      return
    }

    const result = await props.checkTemplatesAccess({ templateIds })

    const accessibleIds = result.accessibleTemplateIds || result
    accessibleTemplateIds.value = new Set(accessibleIds)
  } catch (error) {
    accessibleTemplateIds.value = new Set()
  } finally {
    isCheckingTemplateAccess.value = false
  }
}

const columnHelper = createColumnHelper<CanvasRetrieve>()

const defaultColumns = [
  columnHelper.accessor((row) => row.name, {
    id: 'name',
    cell: (cellCtx) => (
      <CTableInput
        modelValue={cellCtx.getValue()}
        readonly={!!editingColumn.value?.id || isCanvasLocked(cellCtx.row.original.id)}
        state={isEditingCurrentColumn(cellCtx.row.original.id, cellCtx.column.id) ? 'edit' : 'view'}
        valueClass="font-700 underline unerline-solid"
        class="cursor-pointer"
        useFixedEditWidth
        onUpdate:modelValue={async (newValue) => {
          await updateProperty(cellCtx.row.original, 'name', newValue)
          clearEditingColumn()
        }}
        onClick={(e: MouseEvent) => {
          emit('open', cellCtx.row.original, isModifierClick(e))
        }}
        onEdit={() => {
          editingColumn.value = {
            id: cellCtx.row.original.id,
            column: cellCtx.column.id,
          }
        }}
        onCancel={clearEditingColumn}
      >
        {{
          prefix: () => {
            const thumbnailUrl = cellCtx.row.original.thumbnail_url ?? cellCtx.row.original.metadata?.thumbnail

            return (
              <div class="flex items-center">
                <CThumbnail
                  class="mr-2"
                  thumbnailUrl={thumbnailUrl}
                  item={cellCtx.row.original}
                  themeVars={themeVars.value}
                  enableThumbnails={false}
                />
                {isCanvasLocked(cellCtx.row.original.id) && (
                  <CIcon
                    v-tooltip={getCanvasLockTooltip(cellCtx.row.original.id)}
                    icon="lock"
                    color={themeVars.value.primary4}
                    faType="fas"
                    size="20px"
                    class="mr-2"
                  />
                )}
              </div>
            )
          },
        }}
      </CTableInput>
    ),
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.nameTh'),
  }),
  ...(pitcherSettings.value?.enable_indicators
    ? [
        columnHelper.accessor((row) => row.indicators, {
          id: 'progress',
          cell: (cellCtx) => <CSavedCanvasesManagementIndicatorsCell indicators={cellCtx.getValue()} />,
          header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.progressTh'),
          enableSorting: false,
        }),
      ]
    : []),
  columnHelper.accessor((row) => row.template, {
    id: 'template',
    cell: (cellCtx) => {
      const templateId = cellCtx.row.original?.template?.id
      const canvasTags = cellCtx.row.original?.tags
      // If distributed canvas feature is disabled, assume all templates are accessible
      const hasAccess = launchDarkly.value.enable_canvas_core_distributions
        ? templateId
          ? accessibleTemplateIds.value.has(templateId)
          : true
        : true
      // Check if the canvas has system tags (like "sharebox" from share template)
      const hasSystemCanvasTag = hasSystemTag(canvasTags)
      const hideCreate = props.isCanvasCreationDisabled || !hasAccess || hasSystemCanvasTag
      const disableOpen = props.noTabs || !hasAccess

      return (
        <CSavedCanvasesManagementUsedTemplateCell
          name={cellCtx.row.original?.template?.name}
          hideCreate={hideCreate}
          disableOpen={disableOpen}
          onOpen={() => emit('openTemplate', cellCtx.row.original.id, cellCtx.row.original.template.id)}
          onCreate={() => emit('createFromTemplate', cellCtx.row.original.template.id)}
        />
      )
    },
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.templateUsedTh'),
    enableSorting: false,
  }),
  ...(props.isCRM
    ? [
        columnHelper.accessor((row) => row.account, {
          id: 'account',
          cell: (cellCtx) => (
            <CTableMore
              modelValue={[{ label: cellCtx.row.original.account?.name ?? '-', key: cellCtx.row.original.account?.id }]}
              rowIndex={cellCtx.row.index}
              isClickable={false}
            />
          ),
          header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.accountTh'),
          enableSorting: false,
        }),
      ]
    : []),
  columnHelper.accessor((row) => row.tags, {
    id: 'tags',
    cell: (cellCtx) => (
      <CTableTag
        readonly={!!editingColumn.value?.id || isCanvasLocked(cellCtx.row.original.id)}
        modelValue={cellCtx.getValue() ?? []}
        state={isEditingCurrentColumn(cellCtx.row.original.id, cellCtx.column.id) ? 'edit' : 'view'}
        rowIndex={cellCtx.row.index}
        suggestedTagsFetcher={(search) =>
          fetchSuggestedInstanceTags(search, { type: ['canvases'], canvasType: 'canvas' })
        }
        onUpdate:modelValue={async (newValue) => {
          await updateProperty(cellCtx.row.original, 'tags', newValue)
          clearEditingColumn()
        }}
        onEdit={() => {
          editingColumn.value = {
            id: cellCtx.row.original.id,
            column: cellCtx.column.id,
          }
        }}
        onCancel={clearEditingColumn}
      />
    ),
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.tagsTh'),
    enableSorting: false,
    meta: {
      classes: launchDarkly.value.enable_short_tag_column_in_tables ? '!max-w-[200px]' : undefined,
    },
  }),
  columnHelper.accessor((row) => row.is_draft, {
    id: 'is_draft',
    cell: (cellCtx) => (
      <div class="flex items-center">
        <CSwitch
          disabled={!!editingColumn.value?.id || isCanvasLocked(cellCtx.row.original.id)}
          checked={!cellCtx.getValue()}
          onChange={async (newValue: boolean) => await updateProperty(cellCtx.row.original, 'is_draft', !newValue)}
          themeOverrides={{
            railColorActive: themeVars.value.success,
            railColor: themeVars.value.primary6,
          }}
        />
      </div>
    ),
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagementStatusCell.status.ready'),
    enableSorting: false,
  }),
  ...(props.isAnyTypeOfAdmin
    ? [
        columnHelper.accessor((row) => row.owned_by, {
          id: 'owned_by__name',
          cell: (cellCtx) => {
            const canvasId = cellCtx.row.original.id
            const email = cellCtx.row.original.owned_by?.email ?? ''
            const ownedByText = cellCtx.row.original.owned_by?.name || email || ''
            return (
              <CSavedCanvasesManagementOwnerCell
                class="min-w-[280px]"
                editable={props.isAnyTypeOfAdmin}
                myUser={props.myUser}
                owner={{ name: ownedByText, email, value: cellCtx.row.original.owned_by?.id }}
                isEditing={canvasOwnerIdColumnStatus.value[canvasId] === 'editing'}
                isUpdating={canvasOwnerIdColumnStatus.value[canvasId] === 'updating'}
                onUpdate:is-editing={(v: boolean) => {
                  canvasOwnerIdColumnStatus.value = v ? { [canvasId]: 'editing' } : {}
                }}
                onUpdate:owner={async (v: Owner) => {
                  try {
                    canvasOwnerIdColumnStatus.value = { [canvasId]: 'updating' }
                    await updateProperty(cellCtx.row.original, 'owned_by_id', v.value)
                  } finally {
                    canvasOwnerIdColumnStatus.value = {}
                  }
                }}
              />
            )
          },
          header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.ownedByTh'),
        }),
      ]
    : []),
  columnHelper.accessor((row) => row.modified_at, {
    id: 'modified_at',
    cell: (cellCtx) => <div class="min-w-[150px]">{formatDateTime(cellCtx.getValue()) as string}</div>,
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.modifiedAtTh'),
  }),
  columnHelper.accessor((row) => row.name, {
    id: 'actions',
    cell: (cellCtx) => {
      const asyncState = getAsyncDownloadState(cellCtx.cell.row.original)
      return (
        <CSavedCanvasesManagementActionsCell
          canvasId={cellCtx.cell.row.original.id}
          track={props.track}
          hideLock={
            !launchDarkly.value.enable_canvas_locks ||
            (isOrgAdmin.value ? false : isLockedBySomeoneElse(cellCtx.row.original.id))
          }
          hideCopy={
            props.isCanvasCreationDisabled ||
            !launchDarkly.value.allow_canvas_duplication ||
            isCanvasLocked(cellCtx.row.original.id)
          }
          hideShare={props.isSharedLinkCreationDisabled}
          hideEdit={props.hideEdit || isCanvasLocked(cellCtx.row.original.id)}
          hideDelete={isCanvasLocked(cellCtx.row.original.id)}
          hideViewNewTab={isPitcherWkWebView}
          isCopying={copyingInProgress.value.has(cellCtx.cell.row.original.id)}
          isDeleting={deletingInProgress.value.has(cellCtx.cell.row.original.id)}
          isSharing={sharingInProgress.value.has(cellCtx.cell.row.original.id)}
          isDownloading={downloadingInProgress.value.has(cellCtx.cell.row.original.id)}
          hideOwnerReassign={!props.isAnyTypeOfAdmin}
          downloadStatus={asyncState?.status}
          pdfUrl={asyncState?.pdfUrl}
          pptxUrl={asyncState?.pptxUrl}
          isExpired={asyncState?.isExpired}
          onCopy={() => copyCanvas(cellCtx.cell.row.original)}
          onEdit={() => emit('edit', cellCtx.cell.row.original)}
          onDelete={() => deleteCanvas(cellCtx.cell.row.original)}
          onView={() => emit('open', cellCtx.cell.row.original)}
          onLock={() => lockCanvas(cellCtx.cell.row.original.id)}
          onUnlock={() => unlockCanvas(getCanvasLock(cellCtx.cell.row.original.id)?.id)}
          onViewNewTab={() => emit('open', cellCtx.cell.row.original, true)}
          onDownloadCanvas={(fileFormat) => downloadCanvas(cellCtx.cell.row.original, fileFormat)}
          onStartAsyncDownload={() => startAsyncDownload(cellCtx.cell.row.original)}
          onDownloadPdf={() => downloadAsyncFile(asyncState?.pdfUrl, `${cellCtx.cell.row.original.name}.pdf`)}
          onDownloadPptx={() => downloadAsyncFile(asyncState?.pptxUrl, `${cellCtx.cell.row.original.name}.pptx`)}
          onShare={() => shareCanvas(cellCtx.cell.row.original)}
          onOwnerReassign={() => {
            canvasOwnerIdColumnStatus.value = { [cellCtx.cell.row.original.id]: 'editing' }
          }}
          onDropdownOpen={async () => {
            await checkAndRestartPolling(cellCtx.cell.row.original)
          }}
        />
      )
    },
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actionsTh'),
    enableSorting: false,
  }),
] as ColumnDef<Record<string, any>, any>[]

const columns = computed(() =>
  getVisibleColumns(launchDarkly.value.canvas_manager_table_columns, defaultColumns, 'canvases'),
)

// Helper to get async download state from canvas metadata
const getAsyncDownloadState = (canvas: CanvasRetrieve) => {
  if (!launchDarkly.value.enable_scale_content) {
    return undefined
  }
  if (asyncDownloadStarted.value.has(canvas.id)) {
    return {
      status: 'in_progress' as const,
    }
  }
  const metadata = canvas.metadata as any
  const hasUrls = metadata?.pdf_url && metadata?.pptx_url
  const hasJob = metadata?.job_id
  const conversionDate = metadata?.conversion_date ? new Date(metadata.conversion_date) : null
  // Check if URLs are expired (using instance setting or default of 24 hours)
  const expirationHours = pitcherSettings.value?.canvas?.async_download_expiration_hours ?? 24
  const expirationMs = expirationHours * 60 * 60 * 1000
  const isExpired = conversionDate ? Date.now() - conversionDate.getTime() > expirationMs : false
  if (hasUrls) {
    asyncDownloadStarted.value.delete(canvas.id)
    return {
      status: 'completed' as const,
      pdfUrl: metadata.pdf_url,
      pptxUrl: metadata.pptx_url,
      jobId: metadata.job_id,
      isExpired: isExpired,
    }
  }

  // Check if download was started by user action
  if (asyncDownloadStarted.value.has(canvas.id) || hasJob) {
    return {
      status: 'in_progress' as const,
      jobId: metadata.job_id,
    }
  }

  return { status: 'not_started' as const }
}

const TABLE_COLUMNS_SETTINGS_KEY = 'ciq_saved_canvases_management_table_columns_settings'

const { tableColumnsSettings, columnsFromSettings, onColumnsMove, onColumnsSelect, restoreColumns } =
  useTableColumnsSettings(TABLE_COLUMNS_SETTINGS_KEY, columns.value)

const fetchingParams = computed(() => ({
  page_size: pageSize.value,
  page: page.value,
  ordering: ordering.value,
  filters: filters.value,
  search: search.value,
  ...(accountFilterId.value ? { account__id: accountFilterId.value } : {}),
}))

const isBulkToolbarDisabled = computed(
  () =>
    !!copyingInProgress.value.size || deletingInProgress.value.size > 0 || isBulkDeleting.value || isBulkUpdating.value,
)
const isLoadingTable = computed(() => isLoading.value || isBulkDeleting.value)

const fetchFields = [
  'access_type',
  'id',
  'name',
  'tags',
  'indicators',
  'is_draft',
  'modified_at',
  'updated_at',
  'template',
  'is_template',
  'is_section',
  'is_block',
  'thumbnail_url',
  'metadata',
  'folder_id',
  ...(props.isAnyTypeOfAdmin ? ['owned_by'] : []),
  ...(props.isCRM ? ['account'] : []),
]
const filteredData = computed(() => {
  if (!search.value) return fetchedData.value

  const currSearch = (search.value ?? '').toLocaleLowerCase()

  return fetchedData.value.filter(
    (c) =>
      (c.name.toLocaleLowerCase().includes(currSearch) ||
        c?.tags?.some((tag) => tag.toLocaleLowerCase().includes(currSearch))) ??
      false,
  )
})

const isAccountModalVisible = ref(false)
let accountSelectionResolve: ((account: { value: string; label: string } | null) => void) | null = null

function selectAccount(): Promise<Account | null> {
  return new Promise((resolve) => {
    isAccountModalVisible.value = true
    accountSelectionResolve = (account) => {
      resolve(account ? { id: account.value, name: account.label } : null)
    }
  })
}

function resolveAccountSelection(account: { value: string; label: string } | null) {
  if (accountSelectionResolve) {
    accountSelectionResolve(account)
    accountSelectionResolve = null
  }
  isAccountModalVisible.value = false
}

async function updateProperty<T extends keyof CanvasCreateRequest>(
  item: CanvasRetrieve,
  property: T,
  newValue: CanvasCreateRequest[T],
) {
  try {
    const updatedItem = await props.updater({
      id: item.id,
      [property]: newValue,
      fields: fetchFields.join(','),
    })

    const foundIndex = fetchedData.value.findIndex((c) => c.id === item.id)
    if (foundIndex !== -1) {
      // Preserve indicators from initial fetch
      const previousIndicators = fetchedData.value[foundIndex].indicators
      if (previousIndicators) updatedItem.indicators = previousIndicators

      fetchedData.value = [
        ...fetchedData.value.slice(0, foundIndex),
        updatedItem,
        ...fetchedData.value.slice(foundIndex + 1),
      ]
    }
  } catch (e) {
    console.error('canvas update failed:', e)
  }
}

async function deleteCanvas(item: CanvasRetrieve) {
  confirmation.danger({
    hasExit: false,
    title: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.deleteDialog.title'),
    content: () => (
      <CCanvasDeleteDialogContent
        name={item?.name}
        texts={{
          name: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.deleteDialog.content.name'),
          info: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.deleteDialog.content.info'),
        }}
      />
    ),
    confirmText: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.deleteDialog.confirm'),
    cancelText: t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.deleteDialog.cancel'),
    onConfirm: async () => {
      try {
        deletingInProgress.value.add(item.id)
        await props.deleter(item.id)
        onTableChange('itemsChanged')

        toast.success(
          t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.notifications.delete.success', {
            name: item.name,
          }),
        )
      } catch (e) {
        console.error('canvas deletion failed:', e)

        toast.error(
          t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.notifications.delete.error', {
            name: item.name,
          }),
        )
      } finally {
        deletingInProgress.value.delete(item.id)
      }
    },
  })
}

async function bulkDelete() {
  bulkDeleteDialog({
    selectedRows,
    filteredData,
    isBulkDeleting,
    canvasType: 'canvas',
    onComplete: async () => {
      onTableChange('itemsChanged')
    },
  })
}

async function bulkAddTags() {
  bulkAddTagsDialog({
    selectedRows,
    filteredData,
    isBulkUpdating,
    canvasType: 'canvas',
    onComplete: async () => {
      onTableChange('itemsChanged')
    },
  })
}

async function bulkRemoveTags() {
  bulkRemoveTagsDialog({
    selectedRows,
    filteredData,
    isBulkUpdating,
    canvasType: 'canvas',
    onComplete: async () => {
      onTableChange('itemsChanged')
    },
  })
}

async function bulkUpdateMetadata() {
  bulkUpdateMetadataDialog({
    selectedRows,
    selectedItemNames: filteredData.value?.filter((item) => selectedRows.value[item.id]).map((item) => item.name),
    metadataFetcher,
    updater: props.updater,
    onComplete: () => {
      onTableChange('itemsChanged')
    },
    isBulkUpdating,
    canvasType: 'canvas',
  })
}

async function copyCanvas(item: CanvasRetrieve) {
  try {
    copyingInProgress.value.add(item.id)
    let copyIdx = 1
    let copyName = `${item.name} - ${t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.copy')}_${copyIdx}`
    while (fetchedData.value.some((canvas) => canvas.name === copyName)) {
      copyIdx++
      copyName = `${item.name} - ${t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.copy')}_${copyIdx}`
    }

    const { id, is_default, owned_by, created_by, ...toCopyFrom } = await props.byIdFetcher({
      id: item.id,
    })

    let selectedAccount = null

    if (props.isCRM && !!pitcherSettings.value?.canvas?.require_account_on_canvas_creation) {
      isAccountModalVisible.value = true
      selectedAccount = await selectAccount()
      if (!selectedAccount) {
        isAccountModalVisible.value = false
        return
      }
    }

    await props.creator({
      ...toCopyFrom,
      name: copyName,
      account: selectedAccount?.id
        ? {
            id: selectedAccount.id,
            ...(selectedAccount.name && { name: selectedAccount.name }),
          }
        : null,
      ...(launchDarkly.value.copy_context_to_canvas ? { context: toCopyFrom.context } : { context: undefined }),
      tags: toCopyFrom.tags ?? [],
      is_template: toCopyFrom.is_template ?? false,
      template_id: toCopyFrom.template?.id,
    })

    onTableChange('itemsChanged')

    toast.success(
      t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.notifications.duplicate.success', {
        name: item.name,
      }),
    )
  } catch (e) {
    console.error('canvas copy failed:', e)

    toast.error(
      t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.notifications.duplicate.error', {
        name: item.name,
      }),
    )
  } finally {
    copyingInProgress.value.delete(item.id)
  }
}

async function shareCanvas(item: CanvasRetrieve) {
  try {
    if (!launchDarkly.value?.use_advanced_sharing_modal) {
      sharingInProgress.value.add(item.id)
      await copySharingUrlToClipboard(item)
      toast.success(
        t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.notifications.share.success', {
          name: item.name,
        }),
      )
    }
  } catch (e) {
    console.error('canvas share failed:', e)

    toast.error(
      t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.notifications.share.error', { name: item.name }),
    )
  } finally {
    emit('share', item)
    if (!launchDarkly.value?.use_advanced_sharing_modal) {
      sharingInProgress.value.delete(item.id)
    }
  }
}

function isEditingCurrentColumn(id: CanvasRetrieve['id'], column: string) {
  return editingColumn.value?.id === id && editingColumn.value?.column === column
}

function clearEditingColumn() {
  editingColumn.value = null
}

const downloadCanvas = async (canvas: CanvasRetrieve, fileFormat: 'pdf' | 'pptx' = 'pdf') => {
  downloadingInProgress.value.add(canvas.id)

  const canvasMetadata = (await props.byIdFetcher({ id: canvas.id, fields: 'id,metadata' })) ?? {}
  const canvasWithMetadata = { ...canvasMetadata, ...canvas }

  try {
    setTimeout(() => {
      toast.success(
        t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.notifications.download.success', {
          name: canvasWithMetadata.name,
        }),
      )
    }, 100)
    await props.downloadCanvas(canvasWithMetadata, fileFormat)

    if (canvasWithMetadata?.metadata?.pitcher_custom_download_url) {
      return
    }
  } catch (e) {
    toast.error(
      t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.notifications.download.error', {
        name: canvasWithMetadata.name,
      }),
    )
  } finally {
    downloadingInProgress.value.delete(canvasWithMetadata.id)
  }
}

const startAsyncDownload = async (canvas: CanvasRetrieve) => {
  // If regenerating (canvas already has completed status), clear the URLs first
  const asyncState = getAsyncDownloadState(canvas)
  if (asyncState?.status === 'completed') {
    // Clear the PDF/PPTX URLs from metadata to reset status
    const updatedCanvas = {
      ...canvas,
      metadata: {
        ...canvas.metadata,
        pdf_url: undefined,
        pptx_url: undefined,
        job_id: undefined,
        conversion_date: undefined,
      },
    }

    // Update the canvas in fetchedData to reflect the reset state
    const index = fetchedData.value.findIndex((c) => c.id === canvas.id)
    if (index !== -1) {
      fetchedData.value[index] = updatedCanvas
    }

    // Mark this canvas as having an async download started
    asyncDownloadStarted.value.add(canvas.id)

    // Trigger download with the updated canvas (without URLs)
    await downloadCanvas(updatedCanvas, 'pdf')
    await startPollingCanvas(canvas.id)
  } else {
    // Mark this canvas as having an async download started
    asyncDownloadStarted.value.add(canvas.id)

    // This will trigger the async download through the regular download function
    // The downloadCanvas prop function should handle the async download when experiments are enabled
    await downloadCanvas(canvas, 'pdf')
    await startPollingCanvas(canvas.id)
  }
}

const downloadAsyncFile = (url: string | undefined, filename: string) => {
  if (!url) return

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.target = '_blank'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// Refresh canvas context when dropdown opens to get latest download status
async function refreshCanvasMetadata(canvasId: CanvasRetrieve['id']) {
  if (!launchDarkly.value.enable_scale_content) return

  try {
    const updated = await props.byIdFetcher({
      id: canvasId,
      fields: 'id,metadata,modified_at',
    })

    fetchedData.value = fetchedData.value.map((item) =>
      item.id === canvasId
        ? {
            ...item,
            metadata: updated.metadata,
            modified_at: updated.modified_at,
          }
        : item,
    )
  } catch (error) {
    console.error(`Failed to refresh canvas context for ${canvasId}:`, error)
  }
}

function onTableChange(
  event: 'pageChanged' | 'pageSizeChanged' | 'sortChanged' | 'searchChanged' | 'itemsChanged' | 'filterValuesChanged',
  state?: number | SortingState | PaginationState | string | Record<string, any>,
) {
  if (isSmartFoldersView.value) {
    handleSearch(state as string)
    return
  }

  const initialParams = { ...fetchingParams.value }
  let change
  switch (event) {
    case 'pageChanged':
      change = state as PaginationState
      page.value = change.pageIndex + 1
      break
    case 'pageSizeChanged':
      change = state as number
      pageSize.value = change ?? fetchingParams.value.page_size
      page.value = 1
      break
    case 'sortChanged':
      ;[change] = state as SortingState
      ordering.value = change?.desc ? '-' + change.id : (change?.id ?? DEFAULT_ORDERING)
      page.value = 1
      break
    case 'searchChanged':
      change = state as string
      search.value = change
      page.value = 1
      break
    case 'itemsChanged':
      page.value = 1
      break
    case 'filterValuesChanged':
      change = state as Record<string, any>
      filters.value = change
      page.value = 1
      break
  }

  clearEditingColumn()

  if (event === 'itemsChanged' || !isEqual(initialParams, fetchingParams.value)) {
    selectedRows.value = {}
    fetch(fetchingParams.value)
  }
}

/**
 * Enriches canvases with account names when custom objects are used
 * Uses the accounts prop to resolve account names from account IDs
 */
async function enrichCanvasesWithAccountNames(canvases: CanvasRetrieve[]): Promise<CanvasRetrieve[]> {
  // If CRM mode is disabled, return canvases as-is
  if (!props.isCRM) {
    return canvases
  }

  // Find canvases that have account IDs but missing account names
  const canvasesNeedingNames = canvases.filter((canvas) => {
    const account = canvas.account as any
    return account?.id && !account?.name
  })

  if (canvasesNeedingNames.length === 0) {
    return canvases
  }

  // Create a map of account ID -> account name from the accounts prop
  const accountNameMap = new Map(props.accounts.map((account) => [account.value, account.label]))

  // Enrich canvases with resolved account names
  return canvases.map((canvas) => {
    const account = canvas.account as any
    if (account?.id && !account?.name) {
      // Try to find name using either id or value
      const resolvedName = accountNameMap.get(account.id) || accountNameMap.get(account.value)
      if (resolvedName) {
        return {
          ...canvas,
          account: {
            ...account,
            name: resolvedName,
          },
        }
      }
    }
    return canvas
  })
}

async function fetch(params: {
  page_size: number
  page: number
  ordering: string
  search: string
  filters: Record<string, any>
  account__id?: string
}) {
  try {
    isLoading.value = true
    const requestParams = params.search ? params : omit(params, 'search')

    const dataFilters: Record<string, any> = {}
    const metaDataFilters: Record<string, any> = {}

    Object.entries(requestParams.filters).forEach(([key, value]) => {
      if (dataFilterComponents.value.some((component) => component.name === key)) {
        dataFilters[key] = value
      } else {
        metaDataFilters[key] = value
      }
    })

    const normalizedMetaDataFilters = normalizeFilterParams(metaDataFilters)

    const { results, count } = await props.fetcher({
      ...requestParams,
      filters: normalizedMetaDataFilters,
      ...dataFilters,
      is_section: false,
      is_template: false,
      fields: fetchFields.join(','),
      ...(accountFilterId.value ? { account__id: accountFilterId.value } : {}),
    })

    totalCount.value = count
    fetchedData.value = await enrichCanvasesWithAccountNames(results)
  } catch (e) {
    console.error(e)
  } finally {
    isLoading.value = false
  }
}

function onBulkDistribution() {
  bulkUpdateDistributionDialog({
    selectedCount: tableRef.value?.selectedRowsCount || 0,
    isBulkUpdating,
    canvasType: 'canvas',
    selectedRows,
    onComplete: async () => {
      await onTableChange('itemsChanged')
    },
  })
}

watch(
  fetchedData,
  () => {
    checkTemplateAccessForCurrentData()
  },
  { immediate: true },
)

watch(accountFilterId, () => {
  // refetch if we want to scope the canvases by account
  onTableChange('itemsChanged')
})

onMounted(async () => {
  try {
    isLoading.value = true
    fetchTemplates()
    await fetch(fetchingParams.value)
  } catch (e) {
    console.error(e)
    isLoading.value = false
  }
})

onBeforeUnmount(() => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
  pollingCanvases.value.clear()
})

defineExpose({
  refetch: () => fetch(fetchingParams.value),
})
</script>
